import { useEffect, useState } from "react";
import {
  Box,
  Chip,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  SnackbarContent,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { InfoOutlined as InfoIcon } from "@mui/icons-material";
import type {
  FacilityViewSlice,
  FieldGovernanceMap,
  VendorProfileVendor,
} from "../types";
import { ProfileEditsPanel } from "./ProfileEditsPanel";
import type { VendorCategory } from "@/services/vendorCategories";
import { ChangeRequestsPanel } from "./ChangeRequestsPanel";
import { SystemMonitoringPanel } from "./SystemMonitoringPanel";
import { EditDrawer, EditTarget } from "./EditDrawer";

type Props = {
  facilities: FacilityViewSlice[];
  primaryFacilityId: string;
  vendor: VendorProfileVendor | null | undefined;
  governance: Record<string, FieldGovernanceMap>;
  isInternal?: boolean;
  categories?: VendorCategory[];
};

type TabKey = "profile" | "requests" | "system";

export function PostOnboardingGovernance({
  facilities,
  primaryFacilityId,
  vendor,
  governance,
  isInternal = false,
  categories,
}: Props) {
  const [tab, setTab] = useState<TabKey>("profile");
  const [target, setTarget] = useState<EditTarget | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [selectedFacilityId, setSelectedFacilityId] = useState(primaryFacilityId);

  const selected =
    facilities.find((f) => f.profile.id === selectedFacilityId) ?? facilities[0];
  const showSwitcher = facilities.length > 1;
  const facilityGovernance = governance[selected.profile.id] ?? {};

  useEffect(() => {
    const stillPresent = facilities.some(
      (f) => f.profile.id === selectedFacilityId,
    );
    if (!stillPresent) setSelectedFacilityId(primaryFacilityId);
  }, [facilities, primaryFacilityId, selectedFacilityId]);

  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor: "#E0E4EF",
        borderRadius: "12px",
        overflow: "hidden",
        mt: 3,
        p: 4,
      }}
    >
      <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
        Post-onboarding governance
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
        Request changes to your profile. Some details save immediately, some
        need a verification code, and some are sent for approval.
      </Typography>

      {showSwitcher && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            Facility
          </Typography>
          <Select
            size="small"
            value={selectedFacilityId}
            onChange={(e) => setSelectedFacilityId(e.target.value)}
            sx={{ minWidth: 280 }}
            inputProps={{ "aria-label": "Select facility to edit" }}
          >
            {facilities.map((f) => (
              <MenuItem key={f.profile.id} value={f.profile.id}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>{f.profile.display_name}</span>
                  {f.profile.id === primaryFacilityId && (
                    <Chip
                      size="small"
                      label="PRIMARY"
                      sx={{
                        height: 18,
                        fontSize: 10,
                        fontWeight: 700,
                        bgcolor: "#EBEEFE",
                        color: "#2B44C1",
                      }}
                    />
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mt: 2, borderBottom: 1, borderColor: "#E0E4EF" }}
      >
        <Tab value="profile" label="Profile & Edits" />
        <Tab
          value="requests"
          label={`Change Requests (${selected.change_requests.length})`}
        />
        <Tab value="system" label="System Monitoring" />
      </Tabs>

      <Box sx={{ pt: 3 }}>
        {tab === "profile" && (
          <ProfileEditsPanel
            facilityId={selected.profile.id}
            facility={selected.profile}
            vendor={vendor}
            governance={facilityGovernance}
            contactEmails={selected.contact_emails}
            documents={selected.documents}
            changeRequests={selected.change_requests}
            operational={selected.operational}
            isInternal={isInternal}
            categories={categories}
            onEdit={setTarget}
          />
        )}
        {tab === "requests" && (
          <ChangeRequestsPanel
            changeRequests={selected.change_requests}
            governance={facilityGovernance}
          />
        )}
        {tab === "system" && (
          <SystemMonitoringPanel
            facilityId={selected.profile.id}
            operational={selected.operational}
          />
        )}
      </Box>

      <EditDrawer
        target={target}
        isInternal={isInternal}
        onClose={() => setTarget(null)}
        onApplied={(msg) => setToast(msg)}
        onNotify={(msg) => setToast(msg)}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <SnackbarContent
          message={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <InfoIcon sx={{ fontSize: 20 }} />
              <span>{toast}</span>
            </Box>
          }
        />
      </Snackbar>
    </Paper>
  );
}

export default PostOnboardingGovernance;
